<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import * as Yup from 'yup';
import { onFormErrorScrollFocus } from 'ui/util/onFormError';
import useUrqlUpdate from 'ui/hooks/useUrqlUpdate';

import {
  PortalContactAcceptTermsConditionsDocument as Document,
  type PortalContactAcceptTermsConditionsMutation as Mutation,
  type PortalContactSessionFragmentFragment,
  type PortalContactAcceptTermsConditionsMutationVariables as Variables,
} from '@/graphql/pp/generated/graphql';

const props = defineProps<{
  open: boolean,
  loading?: boolean,
  onLogout:() => void,
  onSuccess:(data: PortalContactSessionFragmentFragment) => void,
}>();

const { onSubmitUrql, fetching, error } = useUrqlUpdate<Mutation, Variables>(
  useClientHandle(),
  Document,
  'portalContactAcceptTermsConditions',
);

const onSubmit = () => onSubmitUrql({}, {
  onError: () => {},
  onSuccess: (data) => {
    props.onSuccess(data.portalContactAcceptTermsConditions as PortalContactSessionFragmentFragment);
  },
});

interface FormValues {
  accept: boolean
}

const schema = Yup.object<FormValues>({
  accept: Yup.boolean().nullable()
    .oneOf([true], 'Please accept Terms of Service')
    .required('Please accept Terms of Service'),
});

const state = ref<FormValues>({ accept: false });

</script>
<template>
  <UModal
    :open="open"
    title="Terms of Service"
    description="Please review and accept the Terms of Service to continue."
    :dismissible="false"
    :ui="{
      header: 'sr-only',
    }"
  >
    <template #body>
      <div class="flex flex-col">
        <div class="w-full pt-4">
          <SvgGradGuard class="h-6 mx-auto mb-4" />
        </div>
        <div class="flex-1 overflow-y-scroll">
          <h2 class="text-center text-lg font-semibold leading-6 text-(--ui-primary)">
            Terms of Service
          </h2>
          <div class="prose prose-h1:text-3xl prose-h2:text-xl mx-auto">
            <TermConditionsText />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <UiErrorAlert v-if="error && !fetching" :error="error" class="mb-2" />
      <UForm
        :state="state"
        :schema="schema"
        class="w-full"
        @submit="() => { onSubmit(); }"
        @error="onFormErrorScrollFocus"
      >
        <div class="flex justify-end mb-5">
          <UFormField required name="accept" data-cy="accept-terms-service-group">
            <UCheckbox
              v-model="state.accept"
              :disabled="fetching || loading"
              label="Accept Terms of Service"
              data-cy="accept-terms-service-checkbox"
            />
          </UFormField>
        </div>
        <div class="flex-1 flex justify-between">
          <UButton
            :disabled="fetching || loading"
            :loading="loading"
            variant="link"
            @click="onLogout"
          >
            Logout
          </UButton>
          <UButton
            :disabled="fetching || loading"
            :loading="fetching"
            data-cy="terms-service-accept-button"
            type="submit"
          >
            Accept
          </UButton>
        </div>
      </UForm>
    </template>
  </UModal>
</template>
